import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import GrafanaIcon from "src/components/ui/icons/grafana";
import { getProductName } from "src/utils/brandTheme";

import GrafanaCloudInstructions from "./GrafanaCloudInstructionsContent";
import GrafanaCloudIntegrationForm from "./GrafanaCloudIntegrationForm";

interface GrafanaCloudIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

export default function GrafanaCloudIntegration({ isOpen, onClose, onRefetch, brandTheme }: GrafanaCloudIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [hostingType, setHostingType] = useState<"cloud" | "self_hosted">("cloud");

  // Reset state when dialog reopens so it stays in sync with the form (which remounts)
  useEffect(() => {
    if (isOpen) {
      setHostingType("cloud");
      setShowInstructions(false);
    }
  }, [isOpen]);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <GrafanaIcon className="h-6 w-6" />
            Connect Grafana
          </DialogTitle>
          <DialogDescription>
            Connect your Grafana instance to give {productName} access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <GrafanaCloudInstructions productName={productName} hostingType={hostingType} />
            </div>
          </div>

          <GrafanaCloudIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
            onHostingTypeChange={setHostingType}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
